import { useEffect, useMemo } from "react";
import useAppStore from "src/stores/appStore";
import { setHasAddedFilterSelector } from "src/stores/selectors/collectionsSelectors";
import { useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";
import { decodeFiltersFromQueryParam, encodeFiltersToQueryParam } from "src/utils/helpers/filterQueryParams";
import { filtersAreEqual, applyParsedFilter, getActiveFilters, EMPTY_FILTER_STATE } from "./FilterPanel.utils";

const useFilter = ({ filters, setFilters, filterQueryParam }) => {
  const setHasAddedFilter = useAppStore(setHasAddedFilterSelector);
  const [searchParams, setSearchParams] = useSearchParams();

  const debouncedSetSearchParams = useMemo(
    () => debounce((filtersToSync) => {
      if (!filterQueryParam) return;
      const newParams = new URLSearchParams(searchParams);
      const encoded = encodeFiltersToQueryParam(filtersToSync);

      if (encoded) {
        newParams.set(filterQueryParam, encoded);
      } else {
        newParams.delete(filterQueryParam);
      }
      setSearchParams(newParams);
    }, 300),
    [searchParams, setSearchParams, filterQueryParam]
  );

  useEffect(() => {
    if (!setFilters || !filterQueryParam) return;

    const filtersParam = searchParams.get(filterQueryParam);
    const activeFilters = getActiveFilters(filters);

    if (activeFilters.length > 0 && !filtersParam) {
      debouncedSetSearchParams(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!setFilters || !filterQueryParam) return;
    const filtersParam = searchParams.get(filterQueryParam);
    const parsedFilters = decodeFiltersFromQueryParam(filtersParam);
    if (!parsedFilters) return;

    setFilters(prevFilters => {
      const currentActive = getActiveFilters(prevFilters);

      if (filtersAreEqual(currentActive, parsedFilters)) {
        return prevFilters;
      }

      return prevFilters.map(filter => applyParsedFilter(filter, parsedFilters));
    });
  }, [filterQueryParam, searchParams, debouncedSetSearchParams]);

  const handleChangeFilterField = (value, filter, operator) => {
    setFilters(prevFilters => {
      const newFilters = prevFilters.map(f => f.key === filter.key ? {
        ...f,
        value,
        operator: operator || f.operator || null,
        isActive: value ? true : f.isActive
      } : f);
      debouncedSetSearchParams(newFilters);
      return newFilters;
    }
    );
  };

  const handleRemoveFilter = (filterKey) => {
    setFilters(prevFilters => {
      const newFilters = prevFilters.map(filter =>
        filter.key === filterKey ? { ...filter, ...EMPTY_FILTER_STATE } : filter
      );
      debouncedSetSearchParams(newFilters);
      return newFilters;
    }
    );
  };

  const handleAddFilter = (filterKey) => {
    setFilters(prevFilters => {
      const newFilters = prevFilters.map(filter =>
        filter.key === filterKey ? { ...filter, isActive: true, activeSince: Date.now() } : filter
      );
      debouncedSetSearchParams(newFilters);
      return newFilters;
    }
    );

    setHasAddedFilter(true);
  };

  const handleClearAll = () => {
    setFilters(prevFilters => {
      const newFilters = prevFilters.map(filter => filter.isDefault ? filter : { ...filter, ...EMPTY_FILTER_STATE });
      debouncedSetSearchParams(newFilters);
      return newFilters;
    });
  };

  // Get active filters and sort them by activeSince
  const activeFilters = filters
    .filter(filter => filter.isActive)
    .sort((a, b) => (a.activeSince || 0) - (b.activeSince || 0));

  const inactiveFilters = filters.filter(filter => !filter.isActive);

  return {
    activeFilters,
    inactiveFilters,
    handleChangeFilterField,
    handleRemoveFilter,
    handleClearAll,
    handleAddFilter
  };
};

export default useFilter;
